import { useCallback, useEffect, useRef, useState } from 'react'
import Icon from './Icon.jsx'

/**
 * Unterschriftsfeld für Finger oder Stift.
 *
 * Bewusst ohne Bibliothek. Die Striche werden nicht als Pixel gespeichert,
 * sondern als Punktfolgen in Breiten-Einheiten (x und y jeweils geteilt durch
 * die Feldbreite). Das hat drei Vorteile:
 *
 *  1. Das Feld darf seine Größe ändern — Vollbild, Drehen des Geräts — ohne
 *     dass die Unterschrift verloren geht oder verzerrt.
 *  2. Das fertige PNG wird unabhängig von der Bildschirmgröße in fester,
 *     hoher Auflösung erzeugt. Auf dem PDF ist es dadurch immer scharf.
 *  3. Es wird auf den tatsächlich beschriebenen Bereich zugeschnitten, statt
 *     viel leere Fläche mitzuschleppen.
 *
 * Zwei Fallstricke, die hier bedacht sind: die Zeichenfläche wird in
 * Gerätepunkten angelegt (devicePixelRatio), sonst ist der Strich auf dem
 * iPhone verwaschen; und `touch-action: none` verhindert, dass beim Zeichnen
 * die Seite mitscrollt.
 */
export default function Unterschrift({ beiAenderung }) {
  const flaeche = useRef(null)
  const striche = useRef([])
  const laufend = useRef(null)
  const [leer, setLeer] = useState(true)
  const [vollbild, setVollbild] = useState(false)

  // Zeichnet den gespeicherten Verlauf neu — nach jedem Strich und nach jeder
  // Größenänderung.
  const male = useCallback(() => {
    const leinwand = flaeche.current
    if (!leinwand) return

    const kasten = leinwand.getBoundingClientRect()
    if (kasten.width === 0) return

    const verhaeltnis = window.devicePixelRatio || 1
    const breitePx = Math.round(kasten.width * verhaeltnis)
    const hoehePx = Math.round(kasten.height * verhaeltnis)
    if (leinwand.width !== breitePx || leinwand.height !== hoehePx) {
      leinwand.width = breitePx
      leinwand.height = hoehePx
    }

    const stift = leinwand.getContext('2d')
    stift.setTransform(verhaeltnis, 0, 0, verhaeltnis, 0, 0)
    stift.clearRect(0, 0, kasten.width, kasten.height)

    stift.lineWidth = Math.max(2, kasten.width * 0.005)
    stift.lineCap = 'round'
    stift.lineJoin = 'round'
    stift.strokeStyle = '#111111'
    stift.fillStyle = '#111111'

    for (const strich of striche.current) {
      if (strich.length === 1) {
        stift.beginPath()
        stift.arc(strich[0].x * kasten.width, strich[0].y * kasten.width, stift.lineWidth / 2, 0, Math.PI * 2)
        stift.fill()
        continue
      }
      stift.beginPath()
      strich.forEach((p, i) => {
        const x = p.x * kasten.width
        const y = p.y * kasten.width
        if (i === 0) stift.moveTo(x, y)
        else stift.lineTo(x, y)
      })
      stift.stroke()
    }
  }, [])

  useEffect(() => {
    male()
    const beobachter = new ResizeObserver(male)
    if (flaeche.current) beobachter.observe(flaeche.current)
    return () => beobachter.disconnect()
  }, [male, vollbild])

  // Erzeugt das PNG: auf den beschriebenen Bereich zugeschnitten und in
  // fester Auflösung, damit es im PDF sauber aussieht.
  function alsBild() {
    const punkte = striche.current.flat()
    if (punkte.length === 0) return null

    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const p of punkte) {
      if (p.x < minX) minX = p.x
      if (p.x > maxX) maxX = p.x
      if (p.y < minY) minY = p.y
      if (p.y > maxY) maxY = p.y
    }

    const rand = 0.03
    minX -= rand
    minY -= rand
    maxX += rand
    maxY += rand

    // Mindestgröße, damit ein einzelner Punkt kein 2-Pixel-Bild ergibt.
    const breite = Math.max(maxX - minX, 0.2)
    const hoehe = Math.max(maxY - minY, 0.07)

    // Lange Unterschriften nach der Breite skalieren, hohe nach der Höhe.
    const skala = hoehe / breite > 0.45 ? 500 / hoehe : 1200 / breite

    const leinwand = document.createElement('canvas')
    leinwand.width = Math.round(breite * skala)
    leinwand.height = Math.round(hoehe * skala)

    const stift = leinwand.getContext('2d')
    stift.lineWidth = Math.max(3, skala * 0.005)
    stift.lineCap = 'round'
    stift.lineJoin = 'round'
    stift.strokeStyle = '#111111'
    stift.fillStyle = '#111111'
    stift.translate(-minX * skala, -minY * skala)

    for (const strich of striche.current) {
      if (strich.length === 1) {
        stift.beginPath()
        stift.arc(strich[0].x * skala, strich[0].y * skala, stift.lineWidth / 2, 0, Math.PI * 2)
        stift.fill()
        continue
      }
      stift.beginPath()
      strich.forEach((p, i) => {
        if (i === 0) stift.moveTo(p.x * skala, p.y * skala)
        else stift.lineTo(p.x * skala, p.y * skala)
      })
      stift.stroke()
    }

    return leinwand.toDataURL('image/png')
  }

  function punkt(ereignis) {
    const kasten = flaeche.current.getBoundingClientRect()
    return {
      x: (ereignis.clientX - kasten.left) / kasten.width,
      y: (ereignis.clientY - kasten.top) / kasten.width,
    }
  }

  function beginn(ereignis) {
    ereignis.preventDefault()
    flaeche.current.setPointerCapture(ereignis.pointerId)
    laufend.current = [punkt(ereignis)]
    striche.current.push(laufend.current)
    setLeer(false)
    male()
  }

  function zug(ereignis) {
    if (!laufend.current) return
    ereignis.preventDefault()
    laufend.current.push(punkt(ereignis))
    male()
  }

  function ende(ereignis) {
    if (!laufend.current) return
    ereignis.preventDefault()
    laufend.current = null
    beiAenderung(alsBild())
  }

  function loeschen() {
    striche.current = []
    laufend.current = null
    setLeer(true)
    male()
    beiAenderung(null)
  }

  const leinwand = (
    <canvas
      ref={flaeche}
      className="unterschrift__flaeche"
      onPointerDown={beginn}
      onPointerMove={zug}
      onPointerUp={ende}
      onPointerLeave={ende}
      onPointerCancel={ende}
    />
  )

  if (vollbild) {
    return (
      <div className="unterschrift--voll">
        <p className="unterschrift__anleitung">
          Hier unterschreiben. Für noch mehr Platz das Gerät quer halten.
        </p>
        {leinwand}
        <div className="unterschrift__fuss">
          <button className="knopf knopf--leise" type="button" onClick={loeschen} disabled={leer}>
            <Icon name="muell" /> Neu zeichnen
          </button>
          <button className="knopf knopf--haupt" type="button" onClick={() => setVollbild(false)}>
            <Icon name="haken" /> Fertig
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="unterschrift">
      {leinwand}
      <div className="unterschrift__fuss">
        <button className="knopf knopf--geist knopf--klein" type="button" onClick={() => setVollbild(true)}>
          <Icon name="plus" groesse={16} /> Größer
        </button>
        <span className="hinweis">
          {leer ? 'Mit dem Finger unterschreiben' : 'Unterschrift erfasst'}
        </span>
        <button
          className="knopf knopf--geist knopf--klein"
          type="button"
          onClick={loeschen}
          disabled={leer}
        >
          <Icon name="muell" groesse={16} /> Neu
        </button>
      </div>
    </div>
  )
}
